/**
 * Background processor for failed like operations
 * Handles retry logic that persists across browser sessions
 */

/**
 * Initialize background processing on app startup
 * Call this once when the app loads
 */
export const initializeLikeBackgroundProcessor = () => {
  // Process any existing failed operations
  setTimeout(() => processFailedLikeQueue(), 2000);
  
  // Set up periodic processing
  setInterval(() => processFailedLikeQueue(), 60000); // Every minute
  
  // Process on window focus (when user returns to app)
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', () => {
      setTimeout(() => processFailedLikeQueue(), 1000);
    });
    
    // Process when network comes back online
    window.addEventListener('online', () => {
      console.log('🔥 Network online - processing failed like operations');
      setTimeout(() => processFailedLikeQueue(), 1000);
    });
  }
};

/**
 * Process queued failed like operations
 * This is the same function from LikeButton.js but extracted for global use
 */
const processFailedLikeQueue = async () => {
  const queueKey = 'failed_like_operations';
  const queue = JSON.parse(localStorage.getItem(queueKey) || '[]');
  
  if (queue.length === 0) return;
  
  console.log(`🔥 Background processor: Processing ${queue.length} failed like operations`);
  
  // Import the toggleLike function dynamically to avoid circular dependencies
  const { toggleLike } = await import('../services/firebase/forum');
  
  const updatedQueue = [];
  
  for (const operation of queue) {
    const { userId, targetType, targetId, isLiked, likeKey, retryCount } = operation;
    
    // Skip operations older than 2 hours
    if (Date.now() - operation.timestamp > 7200000) {
      console.log('🔥 Background processor: Skipping expired like operation:', operation);
      localStorage.removeItem(likeKey);
      continue;
    }
    
    try {
      const result = await toggleLike(userId, targetType, targetId);
      
      if (result.success) {
        console.log('🔥 Background processor: Like operation successful:', operation);
        localStorage.removeItem(likeKey);
        // Don't add back to queue
      } else {
        if (retryCount < 10) { // More retries for background processing
          // Retry with exponential backoff
          operation.retryCount = retryCount + 1;
          operation.timestamp = Date.now();
          updatedQueue.push(operation);
        } else {
          console.error('🔥 Background processor: Giving up on like operation after 10 retries:', operation);
          localStorage.removeItem(likeKey);
        }
      }
    } catch (error) {
      if (retryCount < 10) {
        operation.retryCount = retryCount + 1;
        operation.timestamp = Date.now();
        updatedQueue.push(operation);
      } else {
        console.error('🔥 Background processor: Giving up on like operation after 10 retries due to error:', error);
        localStorage.removeItem(likeKey);
      }
    }
    
    // Add small delay between operations to avoid overwhelming the server
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  localStorage.setItem(queueKey, JSON.stringify(updatedQueue));
  
  if (updatedQueue.length > 0) {
    console.log(`🔥 Background processor: ${updatedQueue.length} operations still pending`);
  }
};

/**
 * Get count of pending like operations
 */
export const getPendingLikeOperationsCount = () => {
  const queue = JSON.parse(localStorage.getItem('failed_like_operations') || '[]');
  return queue.length;
};

/**
 * Clear all pending like operations (for debugging/testing)
 */
export const clearPendingLikeOperations = () => {
  localStorage.removeItem('failed_like_operations');
  console.log('🔥 Cleared all pending like operations');
};
